// "That was me" on the phone page (protocol 1.10): the action on ignored lines (the
// conversation's "Guest 1 spoke" markers and the Guests sheet), its toast, and the "Marked as me"
// history under the guests. Kept out of main.ts so the page wires it in a few lines.

import type { VoiceKitClient } from "../client";
import { ClipPlayer, markAsMine, MarksModel, mountMarks, sendToAssistant, showToast, type ClipOutput } from "../mine";
import type { ServerMsg } from "../protocol";

export interface PhoneMine {
  marks: MarksModel;
  /** Plays ignored lines' clips on the phone's speaker (for `mountTranscript` and `mountGuests`). */
  clips: ClipPlayer;
  /** For `mountTranscript` and `mountGuests`. */
  thatWasMe(ignoredId: string): Promise<void>;
  /** Kit 0.12: "Send to assistant" on a marked line that was not sent (for `mountTranscript`). */
  send(ignoredId: string): Promise<void>;
  /**
   * For `mountTranscript` and `mountGuests`: false for speech heard on another mic (the PC's,
   * another phone's), which the kit refuses a phone (`forbidden`), so neither Play nor "That was
   * me" is offered there. Speech the page can't place (before it knew of any session) keeps them.
   */
  mayAct(ignoredId: string): boolean;
  /** Feed every kit event here, before the transcript and guests see it. */
  apply(m: ServerMsg): void;
}

/** A phone's "That was me" or Play on speech heard on another mic: refused (`forbidden`). */
export const OTHER_MIC_MESSAGE = "This was heard on another device's mic. Mark it there.";

/** How many other mics' lines a phone remembers (the kit keeps 50 markable, `mine.max_kept`). */
const FOREIGN_KEPT = 100;

const otherMic = (e: { code?: string }): string | undefined => (e.code === "forbidden" ? OTHER_MIC_MESSAGE : undefined);

/**
 * `client()`: the live client (null while disconnected). `history`: where the "Marked as me" list
 * goes (under the guests); `toastHost`: where the toast shows.
 */
export function phoneMine(
  client: () => VoiceKitClient | null,
  history: HTMLElement,
  toastHost: HTMLElement,
  output?: () => ClipOutput,
): PhoneMine {
  const marks = new MarksModel();
  const notConnected = () => Promise.reject(new Error("Not connected"));
  mountMarks(history, marks, { unlearn: (id) => client()?.speechUnlearn(id) ?? notConnected() });
  // Through the page player's own output (`output`), inside the tap: no second audio context,
  // which iOS could leave silent and which could look like a held call to ./hold.ts.
  const clips = new ClipPlayer((id) => client()?.speechClip(id) ?? Promise.reject(new Error("Not connected")), {
    output,
    onPlaying: (id, playing) => void client()?.speechClipPlaying(id, playing).catch(() => {}),
    onError: (_id, e) => {
      const why = otherMic(e);
      if (why) showToast(toastHost, why, { error: true });
    },
  });
  // Speech heard while another client's session ran (the PC's, another phone's) was on that
  // mic: a phone's sessions always use its own. One session runs at a time, so the session
  // running when the line arrives tells whose mic heard it.
  // Keyed by the kit's epoch: its ids (`i<n>`) are unique only within one run, so a restarted
  // kit's new id never matches an old one. Only the latest few are kept: the kit itself keeps no
  // more than `mine.max_kept` (50) markable.
  const foreign = new Set<string>();
  const key = (id: string) => `${client()?.epoch ?? ""}:${id}`;
  const place = (id: string | undefined) => {
    const c = client();
    if (!id || !c || c.sessionId === null) return; // no session known: can't tell, keep the buttons
    if (c.ownsSession && c.sessionAudio === "client") return;
    foreign.add(key(id));
    if (foreign.size > FOREIGN_KEPT) foreign.delete(foreign.values().next().value!);
  };
  return {
    marks,
    clips,
    thatWasMe(id) {
      const c = client();
      return c ? markAsMine(c, toastHost, id, undefined, otherMic) : notConnected();
    },
    send(id) {
      const c = client();
      return c ? sendToAssistant(c, toastHost, id, undefined, otherMic) : notConnected();
    },
    mayAct: (id) => !foreign.has(key(id)),
    apply(m) {
      if (m.type === "speech.ignored" || m.type === "guest.speech") place(m.ignored_id);
      marks.apply(m);
      // Clips last no longer than voice: stop one playing when it ends.
      if (m.type === "session.ended") clips.stop();
      if (m.type === "welcome" && m.features?.includes("speech_mine")) void client()?.speechMarksGet().catch(() => {});
    },
  };
}
